/**
 * Premium GSAP Animations - Awwwards-level effects
 * Includes: Smooth scroll, text reveals, parallax, magnetic buttons, custom cursor
 */

// Wait for GSAP to load
document.addEventListener('DOMContentLoaded', () => {
  // Check if GSAP is loaded
  if (typeof gsap === 'undefined') {
    console.warn('GSAP not loaded - animations disabled');
    return;
  }

  // Register available GSAP plugins (some are premium/club only)
  const plugins = [];
  if (typeof ScrollTrigger !== 'undefined') plugins.push(ScrollTrigger);
  if (typeof ScrollSmoother !== 'undefined') plugins.push(ScrollSmoother);
  if (typeof SplitText !== 'undefined') plugins.push(SplitText);
  if (typeof ScrollToPlugin !== 'undefined') plugins.push(ScrollToPlugin);

  if (plugins.length) {
    gsap.registerPlugin(...plugins);
  }

  // Initialize all animations
  initSmoothScroll();
  initCustomCursor();
  initTextAnimations();
  initParallaxEffects();
  initRevealAnimations();
  initMagneticButtons();
  initImageReveals();
  initHorizontalScroll();
  initCounterAnimations();
  initMarqueeEffects();
  initNavAnimations();
  initHoverEffects();
  initLoadingAnimation();
});

/**
 * Smooth Scroll with Lenis-like effect
 */
function initSmoothScroll() {
  // Create smooth scroller if ScrollSmoother is available (GSAP Club)
  if (typeof ScrollSmoother !== 'undefined') {
    document.body.classList.add('smooth-scroll-active');

    const smoother = ScrollSmoother.create({
      wrapper: '#smooth-wrapper',
      content: '#smooth-content',
      smooth: 1.5,
      effects: true,
      smoothTouch: 0.1,
    });

    // Store for global access
    window.smoother = smoother;
  } else {
    // Fallback: use native smooth scroll
    document.documentElement.style.scrollBehavior = 'smooth';
  }
}

/**
 * Custom Cursor
 */
function initCustomCursor() {
  const cursor = document.querySelector('.custom-cursor');
  const cursorFollower = document.querySelector('.custom-cursor-follower');

  if (!cursor || !cursorFollower) return;

  let mouseX = 0, mouseY = 0;
  let cursorX = 0, cursorY = 0;
  let followerX = 0, followerY = 0;

  document.addEventListener('mousemove', (e) => {
    mouseX = e.clientX;
    mouseY = e.clientY;
  });

  // Smooth cursor animation
  gsap.ticker.add(() => {
    cursorX += (mouseX - cursorX) * 0.2;
    cursorY += (mouseY - cursorY) * 0.2;
    followerX += (mouseX - followerX) * 0.1;
    followerY += (mouseY - followerY) * 0.1;

    gsap.set(cursor, { x: cursorX, y: cursorY });
    gsap.set(cursorFollower, { x: followerX, y: followerY });
  });

  // Cursor states for links and buttons
  const hoverElements = document.querySelectorAll('a, button, [data-cursor-hover]');
  hoverElements.forEach(el => {
    el.addEventListener('mouseenter', () => {
      cursor.classList.add('cursor-hover');
      cursorFollower.classList.add('cursor-hover');
    });
    el.addEventListener('mouseleave', () => {
      cursor.classList.remove('cursor-hover');
      cursorFollower.classList.remove('cursor-hover');
    });
  });

  // Custom cursor states via data-cursor attribute
  const cursorElements = document.querySelectorAll('[data-cursor]');
  cursorElements.forEach(el => {
    const cursorType = el.dataset.cursor;
    el.addEventListener('mouseenter', () => {
      cursor.classList.add(`cursor-${cursorType}`);
      cursorFollower.classList.add(`cursor-${cursorType}`);
      cursorFollower.setAttribute('data-cursor-text', cursorType === 'view' ? 'View' : cursorType === 'drag' ? 'Drag' : cursorType === 'action' ? '' : '');
    });
    el.addEventListener('mouseleave', () => {
      cursor.classList.remove(`cursor-${cursorType}`);
      cursorFollower.classList.remove(`cursor-${cursorType}`);
      cursorFollower.removeAttribute('data-cursor-text');
    });
  });

  // Hide on touch devices
  if ('ontouchstart' in window) {
    cursor.style.display = 'none';
    cursorFollower.style.display = 'none';
  }
}

/**
 * Text Reveal Animations
 */
function initTextAnimations() {
  // Split text for character/word animations
  const splitTexts = document.querySelectorAll('[data-split-text]');

  splitTexts.forEach(text => {
    const type = text.dataset.splitText || 'chars';

    // Manual split if SplitText not available
    if (typeof SplitText === 'undefined') {
      if (type === 'chars') {
        const chars = text.textContent.split('');
        text.innerHTML = chars.map(char =>
          char === ' ' ? ' ' : `<span class="char">${char}</span>`
        ).join('');
      } else if (type === 'words') {
        const words = text.textContent.split(' ');
        text.innerHTML = words.map(word =>
          `<span class="word"><span class="word-inner">${word}</span></span>`
        ).join(' ');
      } else if (type === 'lines') {
        text.innerHTML = `<span class="line"><span class="line-inner">${text.textContent}</span></span>`;
      }
    } else {
      new SplitText(text, { type: type, charsClass: 'char', wordsClass: 'word', linesClass: 'line' });
    }
  });

  // Animate hero titles
  gsap.utils.toArray('[data-animate="hero-title"]').forEach(title => {
    const chars = title.querySelectorAll('.char');
    if (chars.length) {
      gsap.fromTo(chars,
        {
          y: 100,
          opacity: 0,
          rotateX: -90,
        },
        {
          y: 0,
          opacity: 1,
          rotateX: 0,
          duration: 1,
          ease: 'power4.out',
          stagger: 0.02,
          scrollTrigger: {
            trigger: title,
            start: 'top 80%',
          }
        }
      );
    }
  });

  // Animate section titles with line reveal
  gsap.utils.toArray('[data-animate="title-reveal"]').forEach(title => {
    gsap.fromTo(title,
      {
        clipPath: 'polygon(0 100%, 100% 100%, 100% 100%, 0 100%)',
        y: 50,
      },
      {
        clipPath: 'polygon(0 0%, 100% 0%, 100% 100%, 0 100%)',
        y: 0,
        duration: 1.2,
        ease: 'power4.out',
        scrollTrigger: {
          trigger: title,
          start: 'top 85%',
        }
      }
    );
  });

  // Word by word reveal
  gsap.utils.toArray('[data-animate="words-reveal"]').forEach(el => {
    const words = el.querySelectorAll('.word-inner');
    if (words.length) {
      gsap.fromTo(words,
        { y: '100%' },
        {
          y: '0%',
          duration: 0.8,
          ease: 'power3.out',
          stagger: 0.05,
          scrollTrigger: {
            trigger: el,
            start: 'top 85%',
          }
        }
      );
    }
  });
}

/**
 * Parallax Effects
 */
function initParallaxEffects() {
  // Simple parallax for images
  gsap.utils.toArray('[data-parallax]').forEach(el => {
    const speed = parseFloat(el.dataset.parallax) || 0.5;

    gsap.to(el, {
      y: () => -100 * speed,
      ease: 'none',
      scrollTrigger: {
        trigger: el.parentElement || el,
        start: 'top bottom',
        end: 'bottom top',
        scrub: true,
      }
    });
  });

  // Parallax sections with depth
  gsap.utils.toArray('[data-parallax-section]').forEach(section => {
    const bg = section.querySelector('[data-parallax-bg]');
    const content = section.querySelector('[data-parallax-content]');

    if (bg) {
      gsap.to(bg, {
        y: '-20%',
        ease: 'none',
        scrollTrigger: {
          trigger: section,
          start: 'top bottom',
          end: 'bottom top',
          scrub: true,
        }
      });
    }

    if (content) {
      gsap.fromTo(content,
        { y: 50, opacity: 0 },
        {
          y: 0,
          opacity: 1,
          duration: 1,
          ease: 'power3.out',
          scrollTrigger: {
            trigger: section,
            start: 'top 70%',
          }
        }
      );
    }
  });
}

/**
 * Reveal Animations
 */
function initRevealAnimations() {
  // data-reveal attribute animations (unified reveal system)
  gsap.utils.toArray('[data-reveal]').forEach(el => {
    const type = el.dataset.reveal || 'fade-up';
    const delay = parseFloat(el.dataset.revealDelay) || 0;

    let fromVars = { opacity: 0 };
    let toVars = { opacity: 1, duration: 1, ease: 'power3.out', delay };

    switch(type) {
      case 'fade-up':
        fromVars.y = 60;
        toVars.y = 0;
        break;
      case 'fade-down':
        fromVars.y = -60;
        toVars.y = 0;
        break;
      case 'fade-left':
        fromVars.x = 100;
        toVars.x = 0;
        break;
      case 'fade-right':
        fromVars.x = -100;
        toVars.x = 0;
        break;
      case 'scale':
        fromVars.scale = 0.8;
        toVars.scale = 1;
        break;
      case 'clip':
        fromVars = { clipPath: 'inset(0 100% 0 0)' };
        toVars = { clipPath: 'inset(0 0% 0 0)', duration: 1.2, ease: 'power4.inOut', delay };
        break;
    }

    gsap.fromTo(el, fromVars, {
      ...toVars,
      scrollTrigger: {
        trigger: el,
        start: 'top 85%',
      }
    });
  });

  // Fade up animations
  gsap.utils.toArray('[data-animate="fade-up"]').forEach(el => {
    gsap.fromTo(el,
      { y: 60, opacity: 0 },
      {
        y: 0,
        opacity: 1,
        duration: 1,
        ease: 'power3.out',
        scrollTrigger: {
          trigger: el,
          start: 'top 85%',
        }
      }
    );
  });

  // Fade in animations
  gsap.utils.toArray('[data-animate="fade-in"]').forEach(el => {
    gsap.fromTo(el,
      { opacity: 0 },
      {
        opacity: 1,
        duration: 1,
        ease: 'power2.out',
        scrollTrigger: {
          trigger: el,
          start: 'top 85%',
        }
      }
    );
  });

  // Scale up animations
  gsap.utils.toArray('[data-animate="scale-up"]').forEach(el => {
    gsap.fromTo(el,
      { scale: 0.8, opacity: 0 },
      {
        scale: 1,
        opacity: 1,
        duration: 1,
        ease: 'power3.out',
        scrollTrigger: {
          trigger: el,
          start: 'top 85%',
        }
      }
    );
  });

  // Staggered children animations
  gsap.utils.toArray('[data-animate="stagger"]').forEach(container => {
    const children = container.children;
    const delay = parseFloat(container.dataset.staggerDelay) || 0.1;

    gsap.fromTo(children,
      { y: 40, opacity: 0 },
      {
        y: 0,
        opacity: 1,
        duration: 0.8,
        ease: 'power3.out',
        stagger: delay,
        scrollTrigger: {
          trigger: container,
          start: 'top 85%',
        }
      }
    );
  });

  // Slide in from left
  gsap.utils.toArray('[data-animate="slide-left"]').forEach(el => {
    gsap.fromTo(el,
      { x: -100, opacity: 0 },
      {
        x: 0,
        opacity: 1,
        duration: 1,
        ease: 'power3.out',
        scrollTrigger: {
          trigger: el,
          start: 'top 85%',
        }
      }
    );
  });

  // Slide in from right
  gsap.utils.toArray('[data-animate="slide-right"]').forEach(el => {
    gsap.fromTo(el,
      { x: 100, opacity: 0 },
      {
        x: 0,
        opacity: 1,
        duration: 1,
        ease: 'power3.out',
        scrollTrigger: {
          trigger: el,
          start: 'top 85%',
        }
      }
    );
  });

  // Clip path reveal
  gsap.utils.toArray('[data-animate="clip-reveal"]').forEach(el => {
    gsap.fromTo(el,
      { clipPath: 'inset(0 100% 0 0)' },
      {
        clipPath: 'inset(0 0% 0 0)',
        duration: 1.2,
        ease: 'power4.inOut',
        scrollTrigger: {
          trigger: el,
          start: 'top 80%',
        }
      }
    );
  });
}

/**
 * Magnetic Buttons
 */
function initMagneticButtons() {
  // Support both data-magnetic and .magnetic-btn class
  const magneticElements = document.querySelectorAll('[data-magnetic], .magnetic-btn');

  magneticElements.forEach(el => {
    const strength = parseFloat(el.dataset.magnetic) || 0.3;
    const inner = el.querySelector('[data-magnetic-inner]') || el.querySelector('span') || el;

    el.addEventListener('mousemove', (e) => {
      const rect = el.getBoundingClientRect();
      const x = e.clientX - rect.left - rect.width / 2;
      const y = e.clientY - rect.top - rect.height / 2;

      gsap.to(el, {
        x: x * strength,
        y: y * strength,
        duration: 0.4,
        ease: 'power2.out',
      });

      if (inner !== el) {
        gsap.to(inner, {
          x: x * strength * 0.5,
          y: y * strength * 0.5,
          duration: 0.4,
          ease: 'power2.out',
        });
      }
    });

    el.addEventListener('mouseleave', () => {
      gsap.to(el, {
        x: 0,
        y: 0,
        duration: 0.6,
        ease: 'elastic.out(1, 0.5)',
      });
      if (inner !== el) {
        gsap.to(inner, {
          x: 0,
          y: 0,
          duration: 0.6,
          ease: 'elastic.out(1, 0.5)',
        });
      }
    });
  });
}

/**
 * Image Reveal Effects
 */
function initImageReveals() {
  // Image with overlay reveal
  gsap.utils.toArray('[data-image-reveal]').forEach(container => {
    const img = container.querySelector('img');
    const overlay = container.querySelector('[data-reveal-overlay]');

    const tl = gsap.timeline({
      scrollTrigger: {
        trigger: container,
        start: 'top 80%',
      }
    });

    if (overlay) {
      tl.to(overlay, {
        scaleX: 0,
        transformOrigin: 'right center',
        duration: 1,
        ease: 'power4.inOut',
      });
    }

    if (img) {
      tl.fromTo(img,
        { scale: 1.3 },
        { scale: 1, duration: 1.5, ease: 'power3.out' },
        overlay ? '-=0.5' : 0
      );
    }
  });

  // Mask reveal effect
  gsap.utils.toArray('[data-mask-reveal]').forEach(el => {
    gsap.fromTo(el,
      {
        clipPath: 'polygon(0 0, 0 0, 0 100%, 0 100%)',
      },
      {
        clipPath: 'polygon(0 0, 100% 0, 100% 100%, 0 100%)',
        duration: 1.2,
        ease: 'power4.inOut',
        scrollTrigger: {
          trigger: el,
          start: 'top 80%',
        }
      }
    );
  });
}

/**
 * Horizontal Scroll Sections
 */
function initHorizontalScroll() {
  const horizontalSections = document.querySelectorAll('[data-horizontal-scroll]');

  horizontalSections.forEach(section => {
    const track = section.querySelector('[data-horizontal-track]');
    if (!track) return;

    const items = track.children;
    const totalWidth = Array.from(items).reduce((acc, item) => acc + item.offsetWidth, 0);

    gsap.to(track, {
      x: () => -(totalWidth - window.innerWidth + 100),
      ease: 'none',
      scrollTrigger: {
        trigger: section,
        start: 'top top',
        end: () => `+=${totalWidth}`,
        scrub: 1,
        pin: true,
        anticipatePin: 1,
      }
    });
  });
}

/**
 * Counter Animations
 */
function initCounterAnimations() {
  gsap.utils.toArray('[data-counter]').forEach(counter => {
    const target = parseInt(counter.dataset.counter) || parseInt(counter.textContent);
    const duration = parseFloat(counter.dataset.counterDuration) || 2;
    const suffix = counter.dataset.counterSuffix || '';
    const prefix = counter.dataset.counterPrefix || '';

    const obj = { value: 0 };

    gsap.to(obj, {
      value: target,
      duration: duration,
      ease: 'power2.out',
      scrollTrigger: {
        trigger: counter,
        start: 'top 85%',
      },
      onUpdate: () => {
        counter.textContent = prefix + Math.round(obj.value).toLocaleString() + suffix;
      }
    });
  });
}

/**
 * Marquee Effects
 */
function initMarqueeEffects() {
  gsap.utils.toArray('[data-marquee]').forEach(marquee => {
    const track = marquee.querySelector('[data-marquee-track]');
    if (!track) return;

    const speed = parseFloat(marquee.dataset.marquee) || 50;
    const direction = marquee.dataset.marqueeDirection === 'right' ? 1 : -1;

    // Clone items for seamless loop
    const items = track.innerHTML;
    track.innerHTML = items + items;

    const totalWidth = track.scrollWidth / 2;

    gsap.set(track, { x: direction > 0 ? -totalWidth : 0 });

    gsap.to(track, {
      x: direction > 0 ? 0 : -totalWidth,
      duration: speed,
      ease: 'none',
      repeat: -1,
    });

    // Pause on hover
    marquee.addEventListener('mouseenter', () => {
      gsap.to(track, { timeScale: 0, duration: 0.5 });
    });
    marquee.addEventListener('mouseleave', () => {
      gsap.to(track, { timeScale: 1, duration: 0.5 });
    });
  });
}

/**
 * Navigation Animations
 */
function initNavAnimations() {
  const header = document.querySelector('[data-header]');
  if (!header) return;

  let lastScroll = 0;

  ScrollTrigger.create({
    start: 'top top',
    end: 'max',
    onUpdate: (self) => {
      const currentScroll = self.scroll();

      // Add background on scroll
      if (currentScroll > 100) {
        header.classList.add('header-scrolled');
      } else {
        header.classList.remove('header-scrolled');
      }

      // Hide/show on scroll direction
      if (currentScroll > lastScroll && currentScroll > 200) {
        gsap.to(header, { y: -100, duration: 0.3, ease: 'power2.out' });
      } else {
        gsap.to(header, { y: 0, duration: 0.3, ease: 'power2.out' });
      }

      lastScroll = currentScroll;
    }
  });
}

/**
 * Hover Effects
 */
function initHoverEffects() {
  // Card tilt effect
  document.querySelectorAll('[data-tilt]').forEach(card => {
    const intensity = parseFloat(card.dataset.tilt) || 10;

    card.addEventListener('mousemove', (e) => {
      const rect = card.getBoundingClientRect();
      const x = (e.clientX - rect.left) / rect.width - 0.5;
      const y = (e.clientY - rect.top) / rect.height - 0.5;

      gsap.to(card, {
        rotateY: x * intensity,
        rotateX: -y * intensity,
        transformPerspective: 1000,
        duration: 0.5,
        ease: 'power2.out',
      });
    });

    card.addEventListener('mouseleave', () => {
      gsap.to(card, {
        rotateY: 0,
        rotateX: 0,
        duration: 0.5,
        ease: 'power2.out',
      });
    });
  });

  // Button ripple effect
  document.querySelectorAll('[data-ripple]').forEach(btn => {
    btn.addEventListener('click', (e) => {
      const rect = btn.getBoundingClientRect();
      const ripple = document.createElement('span');
      ripple.className = 'btn-ripple';
      ripple.style.left = `${e.clientX - rect.left}px`;
      ripple.style.top = `${e.clientY - rect.top}px`;
      btn.appendChild(ripple);

      gsap.fromTo(ripple,
        { scale: 0, opacity: 1 },
        {
          scale: 4,
          opacity: 0,
          duration: 0.6,
          ease: 'power2.out',
          onComplete: () => ripple.remove()
        }
      );
    });
  });

  // Image zoom on hover
  document.querySelectorAll('[data-hover-zoom]').forEach(container => {
    const img = container.querySelector('img');
    if (!img) return;

    container.addEventListener('mouseenter', () => {
      gsap.to(img, { scale: 1.1, duration: 0.6, ease: 'power2.out' });
    });

    container.addEventListener('mouseleave', () => {
      gsap.to(img, { scale: 1, duration: 0.6, ease: 'power2.out' });
    });
  });
}

/**
 * Page Transition
 */
function initPageTransitions() {
  const transition = document.querySelector('[data-page-transition]');
  if (!transition) return;

  // Exit animation
  document.querySelectorAll('a:not([target="_blank"]):not([href^="#"])').forEach(link => {
    link.addEventListener('click', (e) => {
      if (link.hostname === window.location.hostname) {
        e.preventDefault();
        const href = link.href;

        gsap.to(transition, {
          scaleY: 1,
          transformOrigin: 'bottom',
          duration: 0.5,
          ease: 'power4.inOut',
          onComplete: () => {
            window.location = href;
          }
        });
      }
    });
  });

  // Enter animation
  window.addEventListener('pageshow', () => {
    gsap.fromTo(transition,
      { scaleY: 1, transformOrigin: 'top' },
      { scaleY: 0, duration: 0.5, ease: 'power4.inOut', delay: 0.1 }
    );
  });
}

/**
 * Loading Animation
 */
function initLoadingAnimation() {
  const loadingScreen = document.getElementById('loading-screen');
  if (!loadingScreen) return;

  // Hide loading screen with animation
  gsap.to(loadingScreen, {
    opacity: 0,
    duration: 0.6,
    delay: 0.3,
    ease: 'power2.out',
    onComplete: () => {
      loadingScreen.style.display = 'none';
      document.body.classList.add('loaded');

      // Trigger initial animations after loading
      ScrollTrigger.refresh();
    }
  });
}

// Refresh ScrollTrigger on window resize
window.addEventListener('resize', () => {
  if (typeof ScrollTrigger !== 'undefined') {
    ScrollTrigger.refresh();
  }
});

// Export for external use
window.UniformicAnimations = {
  refresh: () => ScrollTrigger.refresh(),
  scrollTo: (target) => {
    if (window.smoother) {
      window.smoother.scrollTo(target, true);
    } else {
      gsap.to(window, { scrollTo: target, duration: 1, ease: 'power3.inOut' });
    }
  }
};
